import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getUserStatusByCode, getReferralsCountByCode,
  generateShareLinks, parseRefFromUrl,
  effectivePosition, spotsSkipped,
  REFERRAL_GOAL,
} from './waitlistData';
import './Waitlist.css';

const POLL_MS = 10_000;

export default function WaitlistConfirmation() {
  const [code, setCode] = useState(null);
  const [status, setStatus] = useState(null); // { position, referrals_count, name }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const previousReferralsRef = useRef(0);

  // Read code from URL on mount
  useEffect(() => {
    const c = parseRefFromUrl();
    if (!c) {
      setError('No referral code in the URL. Did you arrive here from a confirmation email?');
      setLoading(false);
      return;
    }
    setCode(c);
  }, []);

  // Initial status fetch
  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const s = await getUserStatusByCode(code);
        if (cancelled) return;
        if (!s) {
          setError("We couldn't find that referral code. Try the link from your welcome email again, or sign up at /waitlist.");
          setStatus(null);
        } else {
          setStatus(s);
          previousReferralsRef.current = s.referrals_count;
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Could not load your status');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  // Poll for live referral updates
  useEffect(() => {
    if (!code || !status) return;
    let cancelled = false;
    const id = setInterval(async () => {
      try {
        const newCount = await getReferralsCountByCode(code);
        if (cancelled) return;
        if (newCount !== previousReferralsRef.current) {
          // Confetti only when crossing the goal threshold for the first time
          if (previousReferralsRef.current < REFERRAL_GOAL && newCount >= REFERRAL_GOAL) {
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 2500);
          }
          previousReferralsRef.current = newCount;
          setStatus(s => s ? { ...s, referrals_count: newCount } : s);
        }
      } catch (_) { /* silent — keep polling */ }
    }, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [code, status]);

  const shareLinks = useMemo(() => code ? generateShareLinks(code) : null, [code]);

  const handleCopy = async () => {
    if (!shareLinks) return;
    try {
      await navigator.clipboard.writeText(shareLinks.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // Fallback: select + execCommand
      try {
        const ta = document.createElement('textarea');
        ta.value = shareLinks.url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (__) { /* give up */ }
    }
  };

  const eff = status ? effectivePosition(status.position, status.referrals_count) : null;
  const skipped = status ? spotsSkipped(status.referrals_count) : 0;
  const goalReached = status && status.referrals_count >= REFERRAL_GOAL;

  return (
    <div className="wl-confirm-page">
      <nav className="wl-confirm-nav">
        <a href="/" className="wl-page__back">&larr; Curx AI</a>
        <div className="wl-page__nav-title">
          <span className="wl-page__nav-dot" />
          CONFIRMED
        </div>
        <span />
      </nav>

      <main className="wl-confirm-main">
        {showConfetti && <Confetti />}

        <article className="wl-confirm-paper">
          <div className="wl-confirm-paper__seal" aria-hidden="true">S</div>

          {loading && (
            <div className="wl-confirm-paper__loading">
              Loading your spot...
            </div>
          )}

          {error && !loading && (
            <div className="wl-confirm-paper__error">
              <h1 className="wl-confirm-paper__error-title">Something went sideways.</h1>
              <p className="wl-confirm-paper__error-msg">{error}</p>
              <a href="/waitlist" className="wl-confirm-paper__error-link">Go to /waitlist</a>
            </div>
          )}

          {status && !loading && (
            <>
              <div className="wl-confirm-paper__date">
                {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>

              <div className="wl-confirm-paper__eyebrow">YOU'RE IN</div>
              {status.name && (
                <p className="wl-confirm-paper__greeting">{status.name},</p>
              )}

              <div className="wl-confirm-paper__position">
                <span className="wl-confirm-paper__pos-hash">#</span>
                <span className="wl-confirm-paper__pos-num">{eff}</span>
              </div>
              <p className="wl-confirm-paper__pos-label">in line for early access</p>

              {skipped > 0 && (
                <p className="wl-confirm-paper__skipped">
                  You've skipped {skipped} spots so far. Original position: #{status.position}.
                </p>
              )}

              <div className="wl-confirm-paper__rule" />

              <section className="wl-confirm-share">
                <h2 className="wl-confirm-share__title">
                  {goalReached ? `You jumped ${spotsSkipped(REFERRAL_GOAL)} spots.` : `Refer ${REFERRAL_GOAL} friends to skip ${spotsSkipped(REFERRAL_GOAL)} spots`}
                </h2>
                <p className="wl-confirm-share__sub">
                  Each friend who joins through your link bumps you 17 spots forward.
                </p>

                <div className="wl-confirm-share__progress">
                  <div className="wl-confirm-share__progress-label">
                    {Math.min(status.referrals_count, REFERRAL_GOAL)} of {REFERRAL_GOAL} friends joined
                  </div>
                  <div className="wl-confirm-share__progress-track">
                    <div
                      className={'wl-confirm-share__progress-fill' + (goalReached ? ' wl-confirm-share__progress-fill--done' : '')}
                      style={{ width: `${Math.min(100, (status.referrals_count / REFERRAL_GOAL) * 100)}%` }}
                    />
                  </div>
                </div>

                <div className="wl-confirm-link">
                  <input
                    type="text"
                    readOnly
                    value={shareLinks?.url || ''}
                    className="wl-confirm-link__input"
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    type="button"
                    className="wl-confirm-link__copy"
                    onClick={handleCopy}
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>

                <div className="wl-confirm-share__buttons">
                  <a
                    href={shareLinks?.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wl-confirm-share__btn"
                  >
                    Share on X
                  </a>
                  <a
                    href={shareLinks?.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wl-confirm-share__btn"
                  >
                    Share on LinkedIn
                  </a>
                  <a
                    href={shareLinks?.email}
                    className="wl-confirm-share__btn"
                  >
                    Share via email
                  </a>
                </div>

                {status.referrals_count > 0 && (
                  <p className="wl-confirm-share__live">
                    Live: {status.referrals_count} friend{status.referrals_count === 1 ? '' : 's'} joined so far
                  </p>
                )}
              </section>

              <div className="wl-confirm-paper__rule" />

              <p className="wl-confirm-paper__note">
                Watch your inbox for updates. We&rsquo;ll email you before launch.<br />
                <em>— Lakshmi, founder</em>
              </p>
            </>
          )}
        </article>
      </main>
    </div>
  );
}

/* ============================================
   CSS-only confetti — 14 amber/cream particles
   that fall + rotate + fade. Triggered on goal hit.
   ============================================ */

function Confetti() {
  const colors = ['#c9a227', '#d4af37', '#faf6ed', '#c9a227', '#d4af37', '#faf6ed',
                  '#c9a227', '#d4af37', '#faf6ed', '#c9a227', '#d4af37', '#faf6ed', '#c9a227', '#d4af37'];
  return (
    <div className="wl-confetti" aria-hidden="true">
      {colors.map((c, i) => (
        <span
          key={i}
          className="wl-confetti__piece"
          style={{
            background: c,
            left: `${4 + i * 7}%`,
            animationDelay: `${i * 35}ms`,
            transform: `rotate(${i * 25}deg)`,
          }}
        />
      ))}
    </div>
  );
}
